import { AfterViewInit, Component, ElementRef, ViewChild, Input, EventEmitter, Output, ChangeDetectionStrategy, inject } from "@angular/core";
import SignaturePad from "signature_pad";
import { ApplicationService } from "../../services/application.service";
import { EnergyConsult } from "../../classes/flow/request/EnergyConsult";
import { CoachEnergyConsultService } from "../../services/coach-energy-consult.service";
import { SnackbarService } from "../../services/snackbar.service";
import { Changes } from "../../classes/flow/base/Changes";
import { Question } from "../../classes/flow/Questionnaire/Question";
import { FormControl } from "@angular/forms";
import { InputErrorComponent } from "../error/error.component";
import { MatButton } from "@angular/material/button";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-signature-input",
  templateUrl: "./signature-input.component.html",
  styleUrls: ["./signature-input.component.less"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [InputErrorComponent, MatButton, TranslatePipe],
})
export class SignatureInputComponent implements AfterViewInit {
  private application = inject(ApplicationService);
  private coachEnergyConsultService = inject(CoachEnergyConsultService);
  private snackbarService = inject(SnackbarService);

  @ViewChild("signature")
  canvas?: ElementRef<HTMLCanvasElement>;

  @Input()
  energyConsult?: EnergyConsult;

  @Input()
  question?: Question;

  @Input()
  control?: FormControl;

  @Output()
  fileSave: EventEmitter<string>;

  signaturePad?: SignaturePad;

  hasDrawn: boolean;

  contentIds?: {
    changes: Changes;
    ffwdcontentid: string;
    ffwdcontentpartid: string;
  };

  filename: string;

  constructor() {
    this.hasDrawn = false;
    this.filename = "signature.png";
    this.fileSave = new EventEmitter<string>();
  }

  async ngAfterViewInit(): Promise<void> {
    this.application.setLoading(true);
    if (this.canvas) this.signaturePad = new SignaturePad(this.canvas.nativeElement);

    if (this.signaturePad)
      this.signaturePad.addEventListener("endStroke", () => {
        this.hasDrawn = true;
      });

    if (this.energyConsult && this.energyConsult.changes && this.energyConsult.ffwdContentId && this.energyConsult.ffwdContentPartId) {
      this.contentIds = {
        changes: this.energyConsult.changes,
        ffwdcontentid: this.energyConsult.ffwdContentId,
        ffwdcontentpartid: this.energyConsult.ffwdContentPartId,
      };
    }

    if (this.question) {
      this.filename = `${this.question.id!}-${this.filename}`;
    }

    this.loadSignature();

    this.application.setLoading(false);
  }

  saveSignature() {
    if (this.hasDrawn && this.signaturePad) {
      const base64 = this.signaturePad?.toDataURL();
      this.uploadSignature(base64);
    }
  }

  dataURLtoFile(dataUrl: string, filename: string): File | undefined {
    const [header, base64] = dataUrl.split(",", 2);

    if (!header || !base64) {
      return undefined;
    }

    const mimeMatch = header.match(/^data:(.*?);base64$/);

    if (!mimeMatch) {
      return undefined;
    }

    const mime = mimeMatch[1];

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new File([bytes], filename, {
      type: mime,
    });
  }

  public async uploadSignature(dataUrl: string) {
    this.application.setLoading(true);
    try {
      const file = this.dataURLtoFile(dataUrl, this.filename);
      if (!file) throw new Error("Invalid Base64");
      if (!this.energyConsult) throw new Error("No EnergyConsult");
      if (!this.contentIds) throw new Error("No Content ID's");

      const formData = new FormData();
      formData.append("contentid", this.contentIds.ffwdcontentid);
      formData.append("contentpartid", this.contentIds.ffwdcontentpartid);
      formData.append("attachment", "true");
      formData.append("file", file);
      await this.coachEnergyConsultService.postRequest("content", formData, { observe: "response", withCredentials: true });
      this.fileSave.emit(this.filename);
      this.hasDrawn = false;
    } catch (error) {
      console.error(error);
      this.snackbarService.error();
    }
    this.application.setLoading(false);
  }

  public async loadSignature() {
    this.application.setLoading(true);
    try {
      if (!this.energyConsult) throw new Error("No Energy Consult");
      const attachments = await this.coachEnergyConsultService.getAttachmentList(this.energyConsult);
      const file = attachments.filter((a: any) => a.name === this.filename)[0];

      if (file) {
        const res = await this.coachEnergyConsultService.postRequest(
          "action",
          {
            FFWDActionID: file.actionIDGet,
          },
          {
            observe: "response",
            responseType: "blob",
          },
        );

        const base = await new Promise((resolve) => {
          this.application.setLoading(true);
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result);
            this.application.setLoading(false);
          };
          reader.readAsDataURL(res.body);
        });

        if (!(typeof base === "string")) throw new Error("Invalid file");

        const base64 = "data:image/png;base64," + base.split(",")[1]!;
        this.signaturePad?.clear();
        await this.signaturePad?.fromDataURL(base64, { width: 300, height: 150 });
      }
    } catch (error) {
      this.snackbarService.error();
    }
    this.application.setLoading(false);
  }
}
